<template>
  <section class="wc-carousel-modern">
    <div class="wc-carousel-modern__viewport">
      <button
        v-if="showArrows"
        class="wc-carousel-modern__arrow wc-carousel-modern__arrow--previous"
        type="button"
        :disabled="atStart"
        @click="scrollByDirection(-1)"
      >
        <font-awesome-icon icon="chevron-left" aria-hidden="true" />
      </button>

      <!-- @scroll.passive fires on every scroll tick; scrollend (registered below) handles
           the final state update after momentum scrolling completes on supporting browsers. -->
      <ul
        ref="trackRef"
        class="wc-carousel-modern__track"
        :aria-label="ariaLabel || title"
        @scroll.passive="updateScrollState"
      >
        <slot></slot>
      </ul>

      <button
        v-if="showArrows"
        class="wc-carousel-modern__arrow wc-carousel-modern__arrow--next"
        type="button"
        :disabled="atEnd"
        @click="scrollByDirection(1)"
      >
        <font-awesome-icon icon="chevron-right" aria-hidden="true" />
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, withDefaults, defineProps } from 'vue';

interface Props {
  title: string;
  titleId?: string;
  ariaLabel?: string;
  scrollAmount?: number;
  showArrows?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  titleId: '',
  ariaLabel: '',
  // 332px ≈ width of one product card (150px) + gap (16px) × 2 + some overlap for context
  scrollAmount: 332,
  showArrows: true,
});

const trackRef = ref<HTMLElement | null>(null);
const atStart = ref(true);
const atEnd = ref(false);
// Plain variable is sufficient — scrollUpdateTimeout is never read in the template
let scrollUpdateTimeout: number | null = null;

const clearScrollUpdateTimeout = () => {
  if (scrollUpdateTimeout !== null) {
    window.clearTimeout(scrollUpdateTimeout);
    scrollUpdateTimeout = null;
  }
};

const updateScrollState = () => {
  if (!trackRef.value) {
    return;
  }

  const currentMaxScroll = Math.max(trackRef.value.scrollWidth - trackRef.value.clientWidth, 0);

  atStart.value = trackRef.value.scrollLeft <= 1;
  atEnd.value = trackRef.value.scrollLeft >= currentMaxScroll - 1;
};

const scrollByDirection = (direction: -1 | 1) => {
  if (!trackRef.value) {
    return;
  }

  trackRef.value.scrollBy({
    left: direction * props.scrollAmount,
    behavior: 'smooth',
  });

  clearScrollUpdateTimeout();
  scrollUpdateTimeout = window.setTimeout(updateScrollState, 180);
};

let trackResizeObserver: ResizeObserver | null = null;
// Guard against the nextTick callback firing after the component has already unmounted,
// which would create a ResizeObserver that never gets cleaned up.
let isMounted = false;
// Tracks whether the scrollend listener was actually registered so onBeforeUnmount
// only removes it when it was added (avoids a no-op remove on early unmount).
let scrollEndAttached = false;

onMounted(() => {
  isMounted = true;

  // Wait for DOM layout before reading scroll state.
  // ResizeObserver keeps arrows in sync when the track width changes
  // (e.g., font loaded, sidebar toggled).
  // scrollend fires after momentum scrolling completes on mobile Safari.
  nextTick(() => {
    if (!isMounted) return;

    updateScrollState();

    if (trackRef.value && typeof ResizeObserver !== 'undefined') {
      trackResizeObserver = new ResizeObserver(() => {
        updateScrollState();
      });
      trackResizeObserver.observe(trackRef.value);
    }
    if (trackRef.value) {
      trackRef.value.addEventListener('scrollend', updateScrollState);
      scrollEndAttached = true;
    }
  });

  window.addEventListener('resize', updateScrollState);
});

onBeforeUnmount(() => {
  isMounted = false;
  clearScrollUpdateTimeout();
  trackResizeObserver?.disconnect();
  trackResizeObserver = null;
  if (scrollEndAttached) {
    trackRef.value?.removeEventListener('scrollend', updateScrollState);
    scrollEndAttached = false;
  }
  window.removeEventListener('resize', updateScrollState);
});
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-carousel-modern {
  display: grid;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
}

.wc-carousel-modern__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0 $pad-4;
}

.wc-carousel-modern__title {
  margin: 0;
  color: var(--dark, $dark);
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.wc-carousel-modern__action {
  padding: 0;
  color: var(--primary, $primary);
  background: transparent;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.2;
  text-decoration: underline;
}

.wc-carousel-modern__action:focus-visible,
.wc-carousel-modern__arrow:focus-visible,
.wc-carousel-modern__track:focus-visible {
  outline: 0.125rem solid var(--primary, $primary);
  outline-offset: 0.125rem;
}

.wc-carousel-modern__viewport {
  position: relative;
  min-width: 0;
}

.wc-carousel-modern__track {
  display: flex;
  max-width: 100%;
  gap: 1rem;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 $pad-4 $pad-2;
  scroll-padding-left: $pad-4;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
}

.wc-carousel-modern__track::-webkit-scrollbar {
  display: none;
}

.wc-carousel-modern__track :deep(> *) {
  flex: 0 0 auto;
  scroll-snap-align: start;
  position: relative;
}

.wc-carousel-modern__track :deep(> *:last-child) {
  scroll-snap-align: end;
}

@media (hover: hover) and (pointer: fine) {
  .wc-carousel-modern__track :deep(> *:has(.wc-card-hover)) {
    transition: box-shadow 0.15s, transform 0.15s;
  }

  .wc-carousel-modern__track :deep(> *:has(.wc-card-hover))::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: 0.5rem;
    pointer-events: none;
    transition: background 0.15s;
  }

  .wc-carousel-modern__track :deep(> *:has(.wc-card-hover):hover) {
    box-shadow: 0 convert-px-to-rem(4) convert-px-to-rem(12) rgba(0, 0, 0, 0.12);
    border-radius: 0.5rem;
  }

  .wc-carousel-modern__track
    :deep(> *:has(.wc-card-hover):hover):not(:has(.wc-menu-dropdown-modern__panel--fixed)) {
    transform: translateY(-2px);
  }

  .wc-carousel-modern__track :deep(> *:has(.wc-card-hover):hover)::before {
    background: rgba(0, 0, 0, 0.04);
  }

  .wc-carousel-modern__track :deep(> *:has(.wc-card-hover:hover))::before {
    background: transparent;
  }
}

.wc-carousel-modern__arrow {
  border: 0.03125rem solid var(--light-darker-4);
  box-shadow: 0 0.25rem 0.25rem rgba(164, 164, 164, 0.25);
  opacity: 0.6;
  position: absolute;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8125rem;
  height: 2.8125rem;
  color: var(--white, $white);
  background: var(--primary, $primary);
  border-radius: 50%;
  cursor: pointer;
  transform: translateY(-50%);
}

.wc-carousel-modern__arrow:hover {
  opacity: 0.85;
}

.wc-carousel-modern__arrow--previous {
  left: -1.37rem;
}

.wc-carousel-modern__arrow--next {
  right: -1.37rem;
}

.wc-carousel-modern__arrow:disabled {
  cursor: default;
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 37.5rem) {
  .wc-carousel-modern {
    gap: 0.75rem;
  }

  .wc-carousel-modern__header {
    padding: 0 $pad-3;
  }

  .wc-carousel-modern__track {
    gap: 0.75rem;
    padding-left: $pad-3;
    padding-right: $pad-3;
    scroll-padding-left: $pad-3;
    -webkit-overflow-scrolling: touch;
  }

  .wc-carousel-modern__arrow {
    display: none;
  }
}
</style>
